"use client";

import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Loader, Mail, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/store/auth-store";
import {
  hasShownNewsletterSoftAsk,
  markNewsletterSoftAskShown,
} from "@/lib/newsletter-soft-ask";
import { useMessages } from "@/lib/i18n/client";
import messages from "./NewsletterSoftAsk.i18n";
import { currentLocale, localeHeaders } from "@/lib/i18n/request";
import { NewsletterCountriesDialog } from "@/components/newsletter/countries-dialog";
import { useEmailField } from "@/lib/email-field";
import { track } from "@/lib/tracking/track";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001/api";

type State = "idle" | "loading" | "success" | "error";

// Guarda de módulo: defensa extra contra doble-disparo síncrono (dos
// triggers en el mismo tick, antes de que el localStorage llegue a
// reflejarse en una segunda lectura). `hasShownNewsletterSoftAsk()` ya
// cubre el caso entre renders/reloads; esto cubre el caso dentro del mismo
// ciclo de eventos.
let shownThisSession = false;

interface SoftAskContentProps {
  toastId: string | number;
}

function SoftAskContent({ toastId }: SoftAskContentProps) {
  const user = useAuthStore((s) => s.user);
  const t = useMessages(messages);
  const { email, rawEmail, setEmail, error: emailError, validate } = useEmailField(user?.email ?? "");
  const [state, setState] = useState<State>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [picking, setPicking] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) setPicking(true);
  };

  const subscribe = async (countries: string[]) => {
    setPicking(false);
    setState("loading");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...localeHeaders() },
        body: JSON.stringify({ email, nombre: user?.name || undefined, language: currentLocale(), countries }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message ?? t.subscribeError);
      }

      setState("success");
      track("newsletter_subscribe", { source: "soft_ask" });
      setTimeout(() => toast.dismiss(toastId), 2200);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t.genericError);
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <div className="flex w-full max-w-sm items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3.5 shadow-lift">
        <CheckCircle className="h-5 w-5 shrink-0 text-primary" />
        <p className="text-sm text-foreground">
          {t.done}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm rounded-2xl border border-border bg-background px-4 py-3.5 shadow-lift">
      {picking && <NewsletterCountriesDialog onConfirm={subscribe} onCancel={() => setPicking(false)} />}
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            {t.title}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t.body}
          </p>

          <form noValidate onSubmit={handleSubmit} className="mt-2.5 flex items-center gap-2">
            <Input
              type="email"
              placeholder={t.emailPlaceholder}
              value={rawEmail}
              onChange={(e) => setEmail(e.target.value)}
              aria-label={t.emailPlaceholder}
              aria-invalid={!!emailError}
              className={`h-9 flex-1 text-sm ${emailError ? "border-red-500" : ""}`}
            />
            <Button
              type="submit"
              disabled={state === "loading"}
              size="sm"
              className="h-9 shrink-0 rounded-lg px-3 text-xs font-semibold"
            >
              {state === "loading" ? (
                <Loader className="h-3.5 w-3.5 animate-spin" />
              ) : (
                t.subscribe
              )}
            </Button>
          </form>
          {emailError && <p className="mt-1.5 text-xs text-red-500">{emailError}</p>}
          {state === "error" && (
            <p className="mt-1.5 text-xs text-red-500">{errorMsg}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => toast.dismiss(toastId)}
          aria-label={t.close}
          className="shrink-0 rounded-full p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Dispara el soft-ask de newsletter (toast no bloqueante) cuando el usuario
 * ya dio una señal de interés real (2do "me interesa" o 2do filtro por la
 * misma comuna). Se invoca directamente desde el punto del trigger — no es
 * un componente montado en el árbol.
 *
 * Nunca se muestra más de una vez por navegador, sin importar si el usuario
 * la aceptó o la descartó.
 */
export function triggerNewsletterSoftAsk(): void {
  if (shownThisSession) return;
  if (hasShownNewsletterSoftAsk()) return;

  // Se marca como "shown" en el momento en que se muestra (no al aceptar/
  // descartar), para que un refresh a mitad de interacción no lo repita.
  shownThisSession = true;
  markNewsletterSoftAskShown();

  toast.custom((id) => <SoftAskContent toastId={id} />, {
    duration: Infinity,
  });
}
